import { useState } from "react";
import { dropshippingProducts } from "@/mocks/dropshipping";

type DashTab = "browse" | "storefront" | "orders" | "earnings";

interface ListedProduct {
  id: number;
  productId: number;
  name: string;
  image: string;
  supplierPrice: string;
  myPrice: string;
  category: string;
  margin: string;
  sold: number;
  status: "active" | "paused";
}

const mockListed: ListedProduct[] = [
  {
    id: 1,
    productId: 4,
    name: "Smart Watch Fitness Tracker Series 6",
    image: "https://public.readdy.ai/ai/img_res/31007ad4accbafc0e2f354c99e355bcc.jpg",
    supplierPrice: "$41.00",
    myPrice: "$68.00",
    category: "Wearables",
    margin: "39%",
    sold: 142,
    status: "active",
  },
  {
    id: 2,
    productId: 6,
    name: "Stainless Steel Water Bottle 1L",
    image: "https://readdy.ai/api/search-image?query=stainless%20steel%20water%20bottle%201%20liter%20sports%20outdoor%20product%20white%20clean%20background%20studio&width=80&height=80&seq=drop6&orientation=squarish",
    supplierPrice: "$9.80",
    myPrice: "$19.99",
    category: "Sports",
    margin: "51%",
    sold: 389,
    status: "active",
  },
  {
    id: 3,
    productId: 9,
    name: "Resistance Bands Set — 5 Levels",
    image: "https://readdy.ai/api/search-image?query=resistance%20bands%20set%20fitness%20exercise%20colorful%20product%20white%20clean%20background%20studio&width=80&height=80&seq=drop9&orientation=squarish",
    supplierPrice: "$11.20",
    myPrice: "$24.99",
    category: "Fitness",
    margin: "55%",
    sold: 210,
    status: "paused",
  },
];

const mockOrders = [
  { id: "#SL-4421", product: "Smart Watch Fitness Tracker Series 6", buyer: "Emma R.", qty: 1, revenue: "$68.00", profit: "$27.00", status: "Delivered", date: "Apr 9, 2026" },
  { id: "#SL-4418", product: "Stainless Steel Water Bottle 1L", buyer: "Tom K.", qty: 3, revenue: "$59.97", profit: "$30.57", status: "Shipped", date: "Apr 9, 2026" },
  { id: "#SL-4415", product: "Smart Watch Fitness Tracker Series 6", buyer: "Yuki M.", qty: 2, revenue: "$136.00", profit: "$54.00", status: "Processing", date: "Apr 8, 2026" },
  { id: "#SL-4410", product: "Stainless Steel Water Bottle 1L", buyer: "Carlos V.", qty: 5, revenue: "$99.95", profit: "$50.95", status: "Delivered", date: "Apr 7, 2026" },
  { id: "#SL-4405", product: "Resistance Bands Set — 5 Levels", buyer: "Priya S.", qty: 2, revenue: "$49.98", profit: "$27.58", status: "Delivered", date: "Apr 6, 2026" },
];

interface Props {
  sellerName: string;
  onClose: () => void;
}

export default function SellerDashboard({ sellerName, onClose }: Props) {
  const [activeTab, setActiveTab] = useState<DashTab>("browse");
  const [listed, setListed] = useState<ListedProduct[]>(mockListed);
  const [browseCategory, setBrowseCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [listingProduct, setListingProduct] = useState<typeof dropshippingProducts[0] | null>(null);
  const [myPrice, setMyPrice] = useState("");
  const [listSuccess, setListSuccess] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<"margin" | "sold" | "price">("margin");

  const categories = ["All", "Electronics", "Kitchen", "Photography", "Wearables", "Furniture", "Sports", "Office", "Fitness", "Home"];

  const filteredBrowse = dropshippingProducts
    .filter((p) => browseCategory === "All" || p.category === browseCategory)
    .filter((p) => p.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === "margin") return parseFloat(b.margin) - parseFloat(a.margin);
      if (sortBy === "sold") return b.sold - a.sold;
      if (sortBy === "price") return parseFloat(a.salePrice.replace("$", "")) - parseFloat(b.salePrice.replace("$", ""));
      return 0;
    });

  const isListed = (id: number) => listed.some((l) => l.productId === id);

  const handleListProduct = (product: typeof dropshippingProducts[0]) => {
    setListingProduct(product);
    const suggested = (parseFloat(product.salePrice.replace("$", "")) * 1.6).toFixed(2);
    setMyPrice(suggested);
  };

  const confirmList = () => {
    if (!listingProduct) return;
    const supplierPrice = parseFloat(listingProduct.salePrice.replace("$", ""));
    const sellerPrice = parseFloat(myPrice);
    const marginPct = Math.round(((sellerPrice - supplierPrice) / sellerPrice) * 100);
    const newItem: ListedProduct = {
      id: Date.now(),
      productId: listingProduct.id,
      name: listingProduct.name,
      image: listingProduct.image,
      supplierPrice: listingProduct.salePrice,
      myPrice: `$${sellerPrice.toFixed(2)}`,
      category: listingProduct.category,
      margin: `${marginPct}%`,
      sold: 0,
      status: "active",
    };
    setListed((prev) => [newItem, ...prev]);
    setListSuccess(listingProduct.id);
    setListingProduct(null);
    setTimeout(() => setListSuccess(null), 3000);
  };

  const toggleStatus = (id: number) => {
    setListed((prev) =>
      prev.map((l) => l.id === id ? { ...l, status: l.status === "active" ? "paused" : "active" } : l)
    );
  };

  const removeFromStorefront = (id: number) => {
    setListed((prev) => prev.filter((l) => l.id !== id));
  };

  const totalRevenue = mockOrders.reduce((a, o) => a + parseFloat(o.revenue.replace("$", "")), 0);
  const totalProfit = mockOrders.reduce((a, o) => a + parseFloat(o.profit.replace("$", "")), 0);
  const totalSold = listed.reduce((a, l) => a + l.sold, 0);

  const tabs: { key: DashTab; label: string; icon: string }[] = [
    { key: "browse", label: "Browse Products", icon: "ri-search-2-line" },
    { key: "storefront", label: "My Storefront", icon: "ri-store-2-line" },
    { key: "orders", label: "Orders", icon: "ri-file-list-3-line" },
    { key: "earnings", label: "Earnings", icon: "ri-money-dollar-circle-line" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col shadow-xl">

        {/* Header */}
        <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20">
              <i className="ri-store-2-line text-white text-lg"></i>
            </div>
            <div>
              <h2 className="text-white font-bold text-lg">Seller Dashboard</h2>
              <p className="text-white/70 text-sm">{sellerName}&apos;s Storefront</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-4 border-b border-stone-100 flex-shrink-0">
          {[
            { label: "Listed Products", value: listed.length, icon: "ri-box-3-line", color: "text-violet-500" },
            { label: "Total Sold", value: totalSold.toLocaleString(), icon: "ri-shopping-cart-2-line", color: "text-amber-500" },
            { label: "Revenue", value: `$${totalRevenue.toFixed(0)}`, icon: "ri-bar-chart-line", color: "text-emerald-500" },
            { label: "Net Profit", value: `$${totalProfit.toFixed(0)}`, icon: "ri-money-dollar-circle-line", color: "text-rose-500" },
          ].map((s) => (
            <div key={s.label} className="flex flex-col items-center py-4 border-r border-stone-100 last:border-r-0">
              <div className="w-7 h-7 flex items-center justify-center mb-1">
                <i className={`${s.icon} ${s.color} text-lg`}></i>
              </div>
              <div className="font-bold text-gray-900 text-lg">{s.value}</div>
              <div className="text-stone-400 text-xs">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-stone-100 flex-shrink-0 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-colors cursor-pointer whitespace-nowrap flex-shrink-0 ${
                activeTab === tab.key
                  ? "border-violet-500 text-violet-600"
                  : "border-transparent text-stone-500 hover:text-gray-700"
              }`}
            >
              <i className={tab.icon}></i>
              {tab.label}
              {tab.key === "storefront" && listed.length > 0 && (
                <span className="bg-violet-100 text-violet-600 text-xs font-bold px-1.5 py-0.5 rounded-full">
                  {listed.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* ── BROWSE TAB ── */}
          {activeTab === "browse" && (
            <div>
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="relative flex-1">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                    <i className="ri-search-line text-stone-400 text-sm"></i>
                  </div>
                  <input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search supplier products..."
                    className="w-full border border-stone-200 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-violet-400 transition-colors"
                  />
                </div>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as "margin" | "sold" | "price")}
                  className="border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-violet-400 bg-white cursor-pointer"
                >
                  <option value="margin">Sort: Highest Margin</option>
                  <option value="sold">Sort: Best Selling</option>
                  <option value="price">Sort: Lowest Price</option>
                </select>
              </div>

              {/* Category pills */}
              <div className="flex flex-wrap gap-2 mb-5">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setBrowseCategory(cat)}
                    className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                      browseCategory === cat
                        ? "bg-violet-500 text-white"
                        : "bg-stone-100 text-stone-600 hover:bg-violet-50 hover:text-violet-600"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              {listSuccess !== null && (
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 text-sm font-medium mb-4">
                  <i className="ri-checkbox-circle-line text-lg"></i>
                  Product added to your storefront! Set your price and start selling.
                </div>
              )}

              {/* Product Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredBrowse.map((product) => {
                  const alreadyListed = isListed(product.id);
                  const supplierPrice = parseFloat(product.salePrice.replace("$", ""));
                  const suggestedPrice = (supplierPrice * 1.6).toFixed(2);
                  const potentialMargin = Math.round(((supplierPrice * 0.6) / (supplierPrice * 1.6)) * 100);

                  return (
                    <div
                      key={product.id}
                      className={`bg-white rounded-xl border p-4 transition-all duration-200 ${
                        alreadyListed ? "border-violet-200 bg-violet-50/30" : "border-stone-100 hover:border-violet-200"
                      }`}
                    >
                      <div className="flex gap-3 mb-3">
                        <div className="w-16 h-16 flex items-center justify-center rounded-lg overflow-hidden flex-shrink-0">
                          <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-xs text-violet-500 font-medium">{product.category}</span>
                          <h4 className="font-semibold text-gray-900 text-xs mt-0.5 line-clamp-2">{product.name}</h4>
                          <div className="text-stone-400 text-xs mt-1">{product.supplier}</div>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-2 mb-3">
                        <div className="bg-stone-50 rounded-lg p-2 text-center">
                          <div className="font-bold text-gray-900 text-xs">{product.salePrice}</div>
                          <div className="text-stone-400 text-xs">Cost</div>
                        </div>
                        <div className="bg-violet-50 rounded-lg p-2 text-center">
                          <div className="font-bold text-violet-600 text-xs">${suggestedPrice}</div>
                          <div className="text-stone-400 text-xs">Suggested</div>
                        </div>
                        <div className="bg-emerald-50 rounded-lg p-2 text-center">
                          <div className="font-bold text-emerald-600 text-xs">{potentialMargin}%</div>
                          <div className="text-stone-400 text-xs">Margin</div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-xs text-stone-400 mb-3">
                        <span><i className="ri-truck-line mr-1"></i>{product.shipping}</span>
                        <span><i className="ri-shopping-cart-2-line mr-1"></i>{product.sold.toLocaleString()} sold</span>
                      </div>

                      {alreadyListed ? (
                        <div className="w-full flex items-center justify-center gap-2 bg-violet-100 text-violet-600 font-semibold py-2 rounded-lg text-xs">
                          <i className="ri-checkbox-circle-line"></i>
                          Listed in Storefront
                        </div>
                      ) : (
                        <button
                          onClick={() => handleListProduct(product)}
                          className="w-full bg-violet-500 hover:bg-violet-600 text-white font-semibold py-2 rounded-lg text-xs transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-add-line mr-1"></i>
                          Add to My Storefront
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── STOREFRONT TAB ── */}
          {activeTab === "storefront" && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900 text-base">My Listed Products</h3>
                <button
                  onClick={() => setActiveTab("browse")}
                  className="flex items-center gap-1.5 text-sm font-semibold text-violet-600 hover:text-violet-700 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line"></i>
                  Add More Products
                </button>
              </div>

              {listed.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-stone-100 mx-auto mb-4">
                    <i className="ri-store-2-line text-stone-400 text-3xl"></i>
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">Your storefront is empty</h4>
                  <p className="text-stone-400 text-sm mb-4">Browse supplier products and add them to start selling</p>
                  <button
                    onClick={() => setActiveTab("browse")}
                    className="bg-violet-500 hover:bg-violet-600 text-white font-semibold px-6 py-2.5 rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Browse Products
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {listed.map((item) => (
                    <div key={item.id} className="flex items-center gap-4 bg-stone-50 rounded-xl p-4 border border-stone-100">
                      <div className="w-14 h-14 flex items-center justify-center rounded-lg overflow-hidden flex-shrink-0">
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900 text-sm truncate">{item.name}</div>
                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                          <span className="text-stone-400 text-xs">Cost: {item.supplierPrice}</span>
                          <span className="text-violet-600 font-bold text-xs">My Price: {item.myPrice}</span>
                          <span className="text-emerald-600 font-semibold text-xs">Margin: {item.margin}</span>
                        </div>
                        <div className="text-stone-400 text-xs mt-1">{item.sold} sold</div>
                      </div>
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <button
                          onClick={() => toggleStatus(item.id)}
                          className={`text-xs font-semibold px-3 py-1 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
                            item.status === "active"
                              ? "bg-green-100 text-green-700 hover:bg-green-200"
                              : "bg-stone-100 text-stone-500 hover:bg-stone-200"
                          }`}
                        >
                          {item.status === "active" ? "Active" : "Paused"}
                        </button>
                        <button
                          onClick={() => removeFromStorefront(item.id)}
                          className="text-xs text-rose-400 hover:text-rose-600 cursor-pointer whitespace-nowrap"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── ORDERS TAB ── */}
          {activeTab === "orders" && (
            <div>
              <h3 className="font-bold text-gray-900 text-base mb-4">Recent Orders</h3>
              <div className="space-y-3">
                {mockOrders.map((order) => (
                  <div key={order.id} className="bg-stone-50 rounded-xl p-4 border border-stone-100">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-bold text-gray-900 text-sm">{order.id}</span>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                            order.status === "Delivered" ? "bg-green-100 text-green-700" :
                            order.status === "Shipped" ? "bg-sky-100 text-sky-700" :
                            "bg-amber-100 text-amber-700"
                          }`}>{order.status}</span>
                        </div>
                        <div className="text-stone-600 text-xs truncate">{order.product}</div>
                        <div className="text-stone-400 text-xs mt-1">
                          Buyer: {order.buyer} · Qty: {order.qty} · {order.date}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-gray-900 font-bold text-sm">{order.revenue}</div>
                        <div className="text-emerald-600 font-semibold text-xs">+{order.profit} profit</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── EARNINGS TAB ── */}
          {activeTab === "earnings" && (
            <div>
              <h3 className="font-bold text-gray-900 text-base mb-5">Earnings Overview</h3>

              {/* Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                {[
                  { label: "This Month Revenue", value: `$${totalRevenue.toFixed(2)}`, icon: "ri-bar-chart-line", color: "text-violet-500", bg: "bg-violet-50" },
                  { label: "Net Profit", value: `$${totalProfit.toFixed(2)}`, icon: "ri-money-dollar-circle-line", color: "text-emerald-500", bg: "bg-emerald-50" },
                  { label: "Avg. Margin", value: "47%", icon: "ri-percent-line", color: "text-amber-500", bg: "bg-amber-50" },
                  { label: "Orders This Month", value: mockOrders.length.toString(), icon: "ri-file-list-3-line", color: "text-sky-500", bg: "bg-sky-50" },
                ].map((s) => (
                  <div key={s.label} className={`${s.bg} rounded-xl p-4`}>
                    <div className="w-8 h-8 flex items-center justify-center mb-2">
                      <i className={`${s.icon} ${s.color} text-xl`}></i>
                    </div>
                    <div className="font-bold text-gray-900 text-lg">{s.value}</div>
                    <div className="text-stone-500 text-xs">{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Per-product breakdown */}
              <h4 className="font-bold text-gray-900 text-sm mb-3">Profit by Product</h4>
              <div className="space-y-3">
                {listed.map((item) => {
                  const cost = parseFloat(item.supplierPrice.replace("$", ""));
                  const price = parseFloat(item.myPrice.replace("$", ""));
                  const profitPerUnit = price - cost;
                  const totalItemProfit = profitPerUnit * item.sold;
                  const maxProfit = 8000;
                  const pct = Math.min((totalItemProfit / maxProfit) * 100, 100);

                  return (
                    <div key={item.id} className="bg-stone-50 rounded-xl p-4 border border-stone-100">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 flex items-center justify-center rounded-lg overflow-hidden flex-shrink-0">
                            <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900 text-xs truncate max-w-[180px]">{item.name}</div>
                            <div className="text-stone-400 text-xs">{item.sold} units sold</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-emerald-600 text-sm">${totalItemProfit.toFixed(2)}</div>
                          <div className="text-stone-400 text-xs">${profitPerUnit.toFixed(2)}/unit</div>
                        </div>
                      </div>
                      <div className="bg-stone-200 rounded-full h-1.5">
                        <div
                          className="bg-violet-400 h-1.5 rounded-full transition-all duration-700"
                          style={{ width: `${pct}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Payout info */}
              <div className="mt-5 bg-gradient-to-r from-violet-500 to-indigo-500 rounded-2xl p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                  <div className="text-white font-bold text-base mb-1">Next Payout</div>
                  <div className="text-white/70 text-sm">Estimated payout on April 15, 2026</div>
                </div>
                <div className="text-right">
                  <div className="text-white font-bold text-3xl">${totalProfit.toFixed(2)}</div>
                  <div className="text-white/70 text-sm">Available balance</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Set Price Modal */}
      {listingProduct && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl p-6">
            <h3 className="font-bold text-gray-900 text-lg mb-1">Set Your Selling Price</h3>
            <p className="text-stone-400 text-sm mb-4">Choose a price above the supplier cost to earn profit.</p>

            <div className="flex items-center gap-3 bg-stone-50 rounded-xl p-3 mb-4 border border-stone-100">
              <div className="w-12 h-12 flex items-center justify-center rounded-lg overflow-hidden flex-shrink-0">
                <img src={listingProduct.image} alt={listingProduct.name} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-900 text-xs truncate">{listingProduct.name}</div>
                <div className="text-stone-400 text-xs">Supplier cost: {listingProduct.salePrice}</div>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Your Selling Price (USD)</label>
              <input
                type="number"
                value={myPrice}
                onChange={(e) => setMyPrice(e.target.value)}
                min={parseFloat(listingProduct.salePrice.replace("$", "")) + 0.01}
                step="0.01"
                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-violet-400 transition-colors"
              />
              {myPrice && parseFloat(myPrice) > parseFloat(listingProduct.salePrice.replace("$", "")) && (
                <div className="mt-2 flex items-center gap-2 text-emerald-600 text-xs font-semibold">
                  <i className="ri-arrow-up-line"></i>
                  Your profit per sale: ${(parseFloat(myPrice) - parseFloat(listingProduct.salePrice.replace("$", ""))).toFixed(2)}
                  {" "}({Math.round(((parseFloat(myPrice) - parseFloat(listingProduct.salePrice.replace("$", ""))) / parseFloat(myPrice)) * 100)}% margin)
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setListingProduct(null)}
                className="flex-1 border border-stone-200 text-stone-600 hover:bg-stone-50 font-semibold py-2.5 rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={confirmList}
                disabled={!myPrice || parseFloat(myPrice) <= parseFloat(listingProduct.salePrice.replace("$", ""))}
                className="flex-1 bg-violet-500 hover:bg-violet-600 text-white font-bold py-2.5 rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                Add to Storefront
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
